import { useEffect, useRef, useState } from "react";
import { Music } from "lucide-react";

export const RhythmGame = ({ difficulty, onDone }) => {
  const { beats, tolerance_ms, interval_ms } = difficulty;
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [hits, setHits] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [finished, setFinished] = useState(false);
  const beatTimes = useRef([]);
  const scoresRef = useRef([]);
  const matched = useRef(new Set());
  const maxComboRef = useRef(0);
  const raf = useRef();
  const finRef = useRef(false);

  useEffect(() => {
    const start = performance.now() + 900;
    beatTimes.current = Array.from({ length: beats }, (_, i) => start + i * interval_ms);
    const loop = () => {
      const now = performance.now();
      setPulse(beatTimes.current.some((bt) => Math.abs(now - bt) < 90));
      if (now > beatTimes.current[beats - 1] + interval_ms + 400) {
        if (!finRef.current) {
          finRef.current = true;
          setFinished(true);
          const acc = scoresRef.current.reduce((a, b) => a + b, 0) / beats;
          const bonus = Math.min(0.15, (maxComboRef.current / beats) * 0.15);
          setTimeout(() => onDone(Math.min(1, acc + bonus)), 400);
        }
        return;
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tap = () => {
    if (finRef.current) return;
    const now = performance.now();
    let best = -1, bestd = 1e9;
    beatTimes.current.forEach((bt, i) => {
      if (matched.current.has(i)) return;
      const d = Math.abs(now - bt);
      if (d < bestd) { bestd = d; best = i; }
    });
    if (best < 0) return;
    if (bestd <= tolerance_ms) {
      matched.current.add(best);
      scoresRef.current.push(1 - (bestd / tolerance_ms) * 0.5);
      setHits((h) => h + 1);
      setCombo((c) => {
        const nc = c + 1;
        if (nc > maxComboRef.current) { maxComboRef.current = nc; setMaxCombo(nc); }
        return nc;
      });
    } else {
      setCombo(0);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="minigame-freestyle">
      <div className="flex items-center justify-between text-xs font-bold text-primary">
        <span className="flex items-center gap-1"><Music size={14} /> {finished ? "Encore!" : "Freestyle Routine"}</span>
        <span data-testid="freestyle-progress">{hits} / {beats} beats</span>
      </div>

      <div className="mt-4 grid place-items-center">
        <div className={`grid h-24 w-24 place-items-center rounded-full transition-all duration-100 ${pulse ? "scale-110 bg-secondary text-white" : "scale-100 bg-muted text-muted-foreground"}`}
          data-testid="freestyle-beat">
          <Music size={34} />
        </div>
      </div>

      <p className="mt-2 text-center text-xs font-bold text-secondary" data-testid="freestyle-combo">Combo ×{combo} · Best ×{maxCombo}</p>

      <button data-testid="freestyle-tap-btn" disabled={finished} onClick={tap}
        className="mt-3 h-16 w-full rounded-xl bg-secondary text-lg font-bold text-white disabled:opacity-40 active:scale-95">
        TAP TO THE BEAT
      </button>
    </div>
  );
};
